import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider, Chip } from '@mui/material';
import {
  formatDateBr as formatDateBrGlobal,
  formatDateTimeBr as formatDateTimeBrGlobal,
} from '../../utils/formatters/dateUtils';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const formatDocument = (value) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return String(value);
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return String(value);
};

const formatBooleanSimNao = (value) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '-';
};

const getSituacaoChipColor = (situacao) => {
  if (!situacao) return 'default';
  const normalized = String(situacao).toLowerCase();
  if (normalized.includes('válida') || normalized.includes('valida')) {
    return 'success';
  }
  if (normalized.includes('negativa')) {
    return 'success';
  }
  if (normalized.includes('vencida') || normalized.includes('expirada')) {
    return 'warning';
  }
  if (normalized.includes('positiva') || normalized.includes('irregular')) {
    return 'error';
  }
  return 'default';
};

const formatDateBr = (value) => {
  if (!value) return '-';
  const formatted = formatDateBrGlobal(value);
  return formatted || String(value);
};

const formatDateTimeBr = (value) => {
  if (!value) return '-';
  const formatted = formatDateTimeBrGlobal(value);
  return formatted || String(value);
};

const normalizeCndData = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.filter((item) => item && typeof item === 'object');
  }

  if (typeof raw === 'object') {
    return [raw];
  }

  return [];
};

export default function ReportPrintCndDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!isDomainEnabled('serpro_cnd')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio CND desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const cndRaw = properties.serpro_cnd;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const records = useMemo(() => normalizeCndData(cndRaw), [cndRaw]);

  if (!records.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro CND encontrado para este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        CND - Certidão Negativa de Débitos
      </Typography>

      {records.map((cnd, index) => {
        const dataNode = cnd && typeof cnd.data === 'object' ? cnd.data : null;
        const dadosCnd =
          dataNode && typeof dataNode.dados_cnd === 'object' ? dataNode.dados_cnd : null;
        const certidao =
          dadosCnd && typeof dadosCnd.certidao === 'object' ? dadosCnd.certidao : null;
        const estruturados =
          dadosCnd && typeof dadosCnd.dados_estruturados === 'object'
            ? dadosCnd.dados_estruturados
            : null;
        const metadata = cnd && typeof cnd.metadata === 'object' ? cnd.metadata : null;

        const situacao =
          (estruturados && estruturados.situacao) || (metadata && metadata.situacao) || null;

        const validade =
          (estruturados && estruturados.validade) ||
          (metadata && metadata.validade) ||
          (certidao && certidao.data_validade && formatDateBr(certidao.data_validade)) ||
          null;

        const tipoDocumento = estruturados && estruturados.tipo_documento;
        const numeroControle = estruturados && estruturados.numero_controle;
        const codigoVerificacao = estruturados && estruturados.codigo_verificacao;

        const contribuinte = estruturados && estruturados.contribuinte;
        const imovel = estruturados && estruturados.imovel;

        const textoCertificacao = estruturados && estruturados.texto_certificacao;

        const dataConsulta = dataNode && dataNode.data_consulta;
        const pdfSalvo = dataNode && dataNode.pdf_salvo;

        const codigoControleCertidao = certidao && certidao.codigo_controle;

        return (
          <Card
            key={index}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'secondary.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              {isSectionEnabled('serpro_cnd', 'resumo_certidao') && (
                <ReportWidget sectionKey="serpro_cnd" widgetKey="cnd_resumo_certidao">
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Resumo da certidão
                        </Typography>
                        <Grid container spacing={1}>
                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'tipo_documento') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'tipo_documento', 'Tipo de documento')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="tipo_documento"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(tipoDocumento)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'numero_controle') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel(
                                  'serpro_cnd',
                                  'numero_controle',
                                  'Número de controle',
                                )}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="numero_controle"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(numeroControle)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled(
                            'serpro_cnd',
                            'resumo_certidao',
                            'codigo_verificacao',
                          ) && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel(
                                  'serpro_cnd',
                                  'codigo_verificacao',
                                  'Código de verificação',
                                )}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="codigo_verificacao"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(codigoVerificacao)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'codigo_controle') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel(
                                  'serpro_cnd',
                                  'codigo_controle',
                                  'Código de controle (certidão)',
                                )}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="codigo_controle"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(codigoControleCertidao)}
                              </ReportStyledField>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'flex-start', md: 'flex-end' },
                            gap: 0.75,
                          }}
                        >
                          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_cnd', 'situacao', 'Situação')}
                            </Typography>
                            {situacao ? (
                              <Chip
                                size="small"
                                color={getSituacaoChipColor(situacao)}
                                label={situacao}
                                sx={{ mt: 0.25 }}
                              />
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>

                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'validade') && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'validade', 'Validade')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="validade"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(validade)}
                              </ReportStyledField>
                            </Box>
                          )}

                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'data_consulta') && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'data_consulta', 'Data da consulta')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="data_consulta"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {dataConsulta ? formatDateTimeBr(dataConsulta) : '-'}
                              </ReportStyledField>
                            </Box>
                          )}

                          {isFieldEnabled('serpro_cnd', 'resumo_certidao', 'pdf_salvo') && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'pdf_salvo', 'PDF salvo')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="pdf_salvo"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatBooleanSimNao(pdfSalvo)}
                              </ReportStyledField>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </ReportWidget>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {isSectionEnabled('serpro_cnd', 'contribuinte') && (
                  <Grid item xs={12} md={6}>
                    <ReportWidget sectionKey="serpro_cnd" widgetKey="cnd_contribuinte">
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Contribuinte
                        </Typography>
                        <Grid container spacing={1.5}>
                          {isFieldEnabled('serpro_cnd', 'contribuinte', 'nome') && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'nome', 'Nome')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="nome"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(contribuinte?.nome)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'contribuinte', 'documento') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'documento', 'Documento')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="documento"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatDocument(contribuinte?.documento)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'contribuinte', 'tipo') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'tipo', 'Tipo')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="tipo"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(contribuinte?.tipo)}
                              </ReportStyledField>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </ReportWidget>
                  </Grid>
                )}

                {isSectionEnabled('serpro_cnd', 'imovel') && (
                  <Grid item xs={12} md={6}>
                    <ReportWidget sectionKey="serpro_cnd" widgetKey="cnd_imovel">
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Imóvel
                        </Typography>
                        <Grid container spacing={1.5}>
                          {isFieldEnabled('serpro_cnd', 'imovel', 'nome') && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'nome', 'Nome do imóvel')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="nome"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(imovel?.nome)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'imovel', 'cib') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'cib', 'CIB')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="cib"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(imovel?.cib)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'imovel', 'uf') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'uf', 'UF')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="uf"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(imovel?.uf)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'imovel', 'municipio') && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'municipio', 'Município')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="municipio"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(imovel?.municipio)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('serpro_cnd', 'imovel', 'area_total') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('serpro_cnd', 'area_total', 'Área total')}
                              </Typography>
                              <ReportStyledField
                                domainKey="serpro_cnd"
                                fieldKey="area_total"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(imovel?.area_total)}
                              </ReportStyledField>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </ReportWidget>
                  </Grid>
                )}
              </Grid>

              {isSectionEnabled('serpro_cnd', 'detalhes_certidao') && (
                <ReportWidget sectionKey="serpro_cnd" widgetKey="cnd_detalhes_certidao">
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Detalhes da certidão
                    </Typography>
                    <Grid container spacing={1.5}>
                      {isFieldEnabled('serpro_cnd', 'detalhes_certidao', 'tipo_contribuinte') && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel(
                              'serpro_cnd',
                              'tipo_contribuinte',
                              'Tipo de contribuinte',
                            )}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="tipo_contribuinte"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(certidao?.tipo_contribuinte)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled(
                        'serpro_cnd',
                        'detalhes_certidao',
                        'contribuinte_certidao',
                      ) && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel(
                              'serpro_cnd',
                              'contribuinte_certidao',
                              'Contribuinte (código)',
                            )}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="contribuinte_certidao"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(certidao?.contribuinte_certidao)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('serpro_cnd', 'detalhes_certidao', 'tipo_certidao') && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('serpro_cnd', 'tipo_certidao', 'Tipo de certidão')}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="tipo_certidao"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(certidao?.tipo_certidao)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('serpro_cnd', 'detalhes_certidao', 'data_emissao') && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel(
                              'serpro_cnd',
                              'data_emissao',
                              'Data de emissão (certidão)',
                            )}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="data_emissao"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {certidao?.data_emissao ? formatDateTimeBr(certidao.data_emissao) : '-'}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('serpro_cnd', 'detalhes_certidao', 'data_validade') && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel(
                              'serpro_cnd',
                              'data_validade',
                              'Data de validade (certidão)',
                            )}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="data_validade"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {certidao?.data_validade ? formatDateBr(certidao.data_validade) : '-'}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('serpro_cnd', 'detalhes_certidao', 'finalidade') && (
                        <Grid item xs={12} sm={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('serpro_cnd', 'finalidade', 'Finalidade')}
                          </Typography>
                          <ReportStyledField
                            domainKey="serpro_cnd"
                            fieldKey="finalidade"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(estruturados?.finalidade)}
                          </ReportStyledField>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </ReportWidget>
              )}

              {isSectionEnabled('serpro_cnd', 'texto_certificacao') && (
                <ReportWidget sectionKey="serpro_cnd" widgetKey="cnd_texto_certificacao">
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Texto da certificação
                    </Typography>
                    {textoCertificacao ? (
                      <ReportStyledField
                        domainKey="serpro_cnd"
                        fieldKey="texto_certificacao"
                        variant="body2"
                        defaultSx={{ whiteSpace: 'pre-line' }}
                        color="text.secondary"
                      >
                        {textoCertificacao}
                      </ReportStyledField>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sem texto de certificação disponível.
                      </Typography>
                    )}
                  </Box>
                </ReportWidget>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
